import React, { useState } from "react";
import { IoClose } from "react-icons/io5";
import { AuthButton } from "../common/AuthButton";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuthStore } from "../../context/useAuthStore";

interface StudentSubjectModalProps {
  fullName: string;
  phoneNumber: string;
  subjects: SubscriptionInfo[];
  viewType: "registered" | "subscribed";
  onClose: () => void;
}

type SubscriptionInfo = {
  subjectName: string;
  subscriptionId: string;
};

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const StudentSubjectModal: React.FC<StudentSubjectModalProps> = ({
  fullName,
  phoneNumber,
  subjects,
  viewType,
  onClose,
}) => {
  const { user, token } = useAuthStore();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const isFormValid = selectedIds.length > 0;

  const toggleCheckbox = (subscriptionId: string) => {
    setSelectedIds((prev) =>
      prev.includes(subscriptionId)
        ? prev.filter((id) => id !== subscriptionId)
        : [...prev, subscriptionId]
    );
  };

  const handleVerify = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isFormValid) {
      toast.error("Please select at least one subject to verify.");
      return;
    }

    setLoading(true);
    if (!user?.userId) {
      toast.error("User not found.");
      return;
    }
    try {
      const response = await axios.put(
        `${API_BASE_URL}/subscription`,
        {
          subscriptionId: selectedIds.join(","),
          status: "Subscribed",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.status === 200 || response.status === 204) {
        toast.success("Subjects subscribed successfully!");
        onClose();
      } else {
        throw new Error("Unexpected response from server.");
      }
    } catch (error: unknown) {
      let message = "Failed to verify subjects. Please try again.";

      if (axios.isAxiosError(error)) {
        message =
          (error.response?.data as { message?: string })?.message ||
          error.message ||
          message;
      } else if (error instanceof Error) {
        message = error.message || message;
      }

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <section
      className="modalSection"
      role="dialog"
      aria-modal="true"
      aria-labelledby="student-subject-modal"
    >
      <div className="modalContainer">
        {/* Modal Header */}
        <div className="flex justify-between items-center gap-2 border-b pb-1 border-gray-200">
          <h2 id="student-subject-modal" className="text-lg font-semibold">
            {viewType === "registered" ? "Registered" : "Subscribed"} Subjects
          </h2>
          <button onClick={onClose} className="modalCloseButton" aria-label="Close modal">
            <IoClose size={20} />
          </button>
        </div>

        {/* Student Info */}
        <div className="logoutModalBody text-left">
          <div className="overflow-x-auto border rounded-md">
            <table className="min-w-full border-collapse">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="tableHeadRow">
                  <th className="tableHeadValue">Student Name</th>
                  <th className="tableHeadValue">Phone Number</th>
                </tr>
              </thead>
              <tbody>
                <tr className="tableBodyRow">
                  <td className="tableBodyValue">{fullName}</td>
                  <td className="tableBodyValue">{phoneNumber}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Subject List */}
          <div className="mt-4 max-h-[calc(100vh-theme(space.80))] overflow-y-auto py-4">
            {subjects.length > 0 ? (
              <form onSubmit={handleVerify}>
                <ul className="list-none space-y-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {subjects.map((subj) => {
                    const isChecked = selectedIds.includes(subj.subscriptionId);
                    return (
                      <li
                        key={subj.subscriptionId}
                        className={`listOfQuestionItem ${
                          isChecked ? "bg-myLabelText text-white" : "bg-white text-myLabelText"
                        } rounded-md px-2 py-1`}
                      >
                        <label
                          htmlFor={`checkbox-${subj.subscriptionId}`}
                          className="flex items-center space-x-2 w-full cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            id={`checkbox-${subj.subscriptionId}`}
                            checked={isChecked}
                            onChange={() => {
                              if (viewType === "registered") toggleCheckbox(subj.subscriptionId);
                            }}
                            disabled={viewType === "subscribed"}
                            className="hidden"
                          />
                          <span className="flex items-center space-x-2">
                            {viewType === "registered" && isChecked && <span className="text-green-500 font-bold">✔</span>}
                            <span>{subj.subjectName}</span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                <div className="pt-6">
                  {viewType === "registered" && (
                  <AuthButton
                    title="Verify"
                    loadingTitle="Verifying..."
                    loading={loading}
                    disabled={!isFormValid || loading}
                  />
                )}
                </div>
              </form>
            ) : (
              <p className="text-sm text-gray-500">No {viewType} subjects.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StudentSubjectModal;
